import { useState } from "react";
import "./SoftwareMcAfee.scss";
import { transformPayload } from "../../Constants";
import { sendToAzureFunction, getFormData, deleteToken } from "../../services/ApiService";
import { handleFormSubmit } from "../../services/FormService";
import storePhoto from "../../resources/storePhoto.png";

interface SoftwareMcAfeeProps {
  onSubmitCallback: (status: string) => void;
  tokenID: string;
  confirmedEmail: string;
  processID: number;
}

const SoftwareMcAfee: React.FC<SoftwareMcAfeeProps> = ({ onSubmitCallback, tokenID, confirmedEmail, processID }) => {
  const [verifizierungscode, setVerifizierungscode] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [securePin, setSecurePin] = useState("");
  const [confirmSecurePin, setConfirmSecurePin] = useState("");
  const [sonstiges, setSonstiges] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [hasConsent, setHasConsent] = useState(false);

  const [emailError, setEmailError] = useState("");
  const [confirmEmailError, setConfirmEmailError] = useState("");
  const [verifizierungscodeError, setVerifizierungscodeError] = useState("");
  const [securePinError, setSecurePinError] = useState("");
  const [confirmSecurePinError, setConfirmSecurePinError] = useState("");
  const [sonstigesError, setSonstigesError] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleCancel = async () => {
    try {
      await deleteToken(tokenID!);
      // After deletion, reload to trigger token validation and show error message
      window.location.reload();
    } catch (error) {
      // Optionally, show an error if deletion failed
      alert("Fehler beim Abbrechen des Vorgangs. Bitte versuchen Sie es erneut.");
    }
  };

  // Validate email
  const validateEmail = (value: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) {
      setEmailError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (!regex.test(value)) {
      setEmailError("Ungültige E-Mail-Adresse.");
      return false;
    } else {
      setEmailError("");
      return true;
    }
  };

  // Validate confirm email
  const validateConfirmEmail = (value: string) => {
    if (!value) {
      setConfirmEmailError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (value.toLowerCase() !== email.toLowerCase()) {
      setConfirmEmailError("E-Mail-Adressen stimmen nicht überein.");
      return false;
    } else {
      setConfirmEmailError("");
      return true;
    }
  };

  // Validate Secure PIN
  const validateSecurePin = (value: string) => {
    const pinRegex = /^\d{4}$/;
    if (!value.trim()) {
      setSecurePinError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (!pinRegex.test(value)) {
      setSecurePinError("Bitte geben Sie eine 4-stellige PIN ein.");
      return false;
    } else if (confirmSecurePin && value !== confirmSecurePin) {
      setConfirmSecurePinError("Die PINs stimmen nicht überein.");
      return false;
    } else {
      setSecurePinError("");
      return true;
    }
  };

  // Validate Confirm Secure PIN
  const validateConfirmSecurePin = (value: string) => {
    if (!value.trim()) {
      setConfirmSecurePinError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (value !== securePin) {
      setConfirmSecurePinError("Die PINs stimmen nicht überein.");
      return false;
    } else {
      setConfirmSecurePinError("");
      return true;
    }
  };

  // Validate phone number
  const validatePhoneNumber = (value: string) => {
    const regex = /^\+?[0-9\s\-]{9,20}$/;
    if (hasConsent && !value) {
      setPhoneNumberError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (hasConsent && !regex.test(value)) {
      setPhoneNumberError("Ungültige Telefonnummer.");
      return false;
    } else {
      setPhoneNumberError("");
      return true;
    }
  };

  // Validate the form
  const validateForm = () => {
    let isValid = true;

    // Validate Verifizierungscode
    if (!verifizierungscode.trim()) {
      setVerifizierungscodeError("Dieses Feld darf nicht leer sein.");
      isValid = false;
    } else {
      setVerifizierungscodeError("");
    }

    // Validate Email
    if (!validateEmail(email)) {
      isValid = false;
    }

    // Validate Confirm Email
    if (!validateConfirmEmail(confirmEmail)) {
      isValid = false;
    }

    // Validate Secure PIN
    if (!validateSecurePin(securePin)) {
      isValid = false;
    }

    // Validate Confirm Secure PIN
    if (!validateConfirmSecurePin(confirmSecurePin)) {
      isValid = false;
    }

    // Validate Phone Number (only if consent is given)
    if (hasConsent && !validatePhoneNumber(phoneNumber)) {
      isValid = false;
    }

    // Validate Sonstiges
    if (!sonstiges.trim()) {
      setSonstigesError("Dieses Feld darf nicht leer sein.");
      isValid = false;
    } else {
      setSonstigesError("");
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    handleFormSubmit(
      e,
      validateForm,
      getFormData,
      transformPayload,
      sendToAzureFunction,
      setIsSubmitted,
      onSubmitCallback,
      tokenID,
      confirmedEmail,
      processID
    );
  };

  return (
    <div className="email-form">
      <img
        src={storePhoto}
        alt="Store Photo"
        style={{ width: "100%", height: "auto", maxWidth: "100%", borderRadius: "10px", marginBottom: "20px" }}
      />
      <h2 style={{ marginTop: 0 }}>Software Installation MC Affe</h2>

      <p style={{ width: "87%", border: "1px solid black", backgroundColor: "#f0f0f0", padding: "15px", borderRadius: "10px", textAlign: "center" }}>
        Bitte geben Sie Ihre Daten für die Durchführung des Services ein
      </p>

      <form onSubmit={handleSubmit}>
        {/* Verifizierungscode */}
        <div className="form-group">
          <label>Aktivierungscode Vom Kassenzettel des Kunden</label>
          <input
            type="text"
            value={verifizierungscode}
            name="verifizierungscode"
            onChange={(e) => setVerifizierungscode(e.target.value)}
            required
          />
          {isSubmitted && verifizierungscodeError && (
            <small className="error-message">{verifizierungscodeError}</small>
          )}
          <small>Notwendig um McAfee zu aktivieren.</small>
        </div>

        {/* Email */}
        <div className="form-group">
          <label>E-Mailadresse</label>
          <input
            type="email"
            value={email}
            name="email"
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {isSubmitted && emailError && (
            <small className="error-message">{emailError}</small>
          )}
          <small>Als Bezug für McAfee und als Loginemailadresse für das Kundenmenü</small>
        </div>

        {/* Confirm Email */}
        <div className="form-group">
          <label>E-Mailadresse bestätigen</label>
          <input
            type="email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            required
          />
          {isSubmitted && confirmEmailError && (
            <small className="error-message">{confirmEmailError}</small>
          )}
        </div>

        {/* Secure PIN Fields */}
        <div style={{ borderTop: "1px solid lightgrey" }}></div>
        <br />

        <div className="form-group">
          <label>Neue PIN</label>
          <input
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            value={securePin}
            name="SecurePIN"
            onChange={(e) => {
              setSecurePin(e.target.value);
              validateSecurePin(e.target.value);
              if (confirmSecurePin) validateConfirmSecurePin(confirmSecurePin);
            }}
            required
          />
          <small>Bitte legen Sie eine sichere 4-stellige PIN fest. Sie benötigen diese PIN für den Zugriff auf Ihre Falldetails und können von einem Mitarbeiter des Geschäfts zur Überprüfung abgefragt werden. Behandeln Sie die PIN vertraulich und geben Sie sie nicht an Dritte weiter.</small>
          {isSubmitted && securePinError && (
            <small className="error-message">{securePinError}</small>
          )}
        </div>

        <div className="form-group">
          <label>Neue PIN bestätigen</label>
          <input
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            value={confirmSecurePin}
            onChange={(e) => {
              setConfirmSecurePin(e.target.value);
              validateConfirmSecurePin(e.target.value);
            }}
            required
          />
          <small>Bitte bestätigen Sie Ihre 4-stellige PIN.</small>
          {isSubmitted && confirmSecurePinError && (
            <small className="error-message">{confirmSecurePinError}</small>
          )}
        </div>

        <br />
        <div style={{ borderTop: "1px solid lightgrey" }}></div>
        <br />

        {/* Sonstiges */}
        <div className="form-group">
          <label>Sonstiges / Kundenwunsch</label>
          <textarea
            value={sonstiges}
            name="sonstiges"
            onChange={(e) => setSonstiges(e.target.value)}
            rows={3}
            required
          ></textarea>
          {isSubmitted && sonstigesError && (
            <small className="error-message">{sonstigesError}</small>
          )}
        </div>

        {/* Consent Checkbox */}
        <div className="form-group">
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={hasConsent}
              onChange={(e) => {
                setHasConsent(e.target.checked);
                if (!e.target.checked) {
                  setPhoneNumber("");
                  setPhoneNumberError("");
                }
              }}
            />
            <span className="checkmark"></span>
            Ich stimme zu, dass ich für Angelegenheiten im Zusammenhang mit dieser Anfrage kontaktiert werden darf.
          </label>
        </div>

        {/* Phone Number */}
        {hasConsent && (
          <div className="form-group">
            <label>Telefonnummer</label>
            <input
              type="tel"
              value={phoneNumber}
              name="HandyNummer"
              onChange={(e) => setPhoneNumber(e.target.value)}
              required={hasConsent}
            />
            <small>Bitte geben Sie eine gültige Telefonnummer ein.</small>
            {isSubmitted && phoneNumberError && (
              <small className="error-message">{phoneNumberError}</small>
            )}
          </div>
        )}

        <button type="submit">Absenden</button>
        <button type="button" onClick={handleCancel} style={{ backgroundColor: "red", marginBottom: 0 }}>
          Abbrechen
        </button>
        <small style={{ color: "#777" }}>Durch das Abbrechen wird der Vorgang gestoppt und Ihre Daten werden nicht weiter verwendet</small>
      </form>
    </div>
  );
};

export default SoftwareMcAfee;